import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getNewBusinessReportByMonth, downloadNewBusinessReportByMonthCSV, getInvoicePaymentsReport } from "../../../actions/report";
import { Card, DatePicker, Table, Skeleton, Space,Breadcrumb, Divider, Button } from "antd";
import { DownloadOutlined } from '@ant-design/icons';
import moment from "moment";
import ReactExport from "react-export-excel";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;
const NewBusinessReportByMonth = () => {
  const [dailyData, setDailyData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [cashSummary, setCashSummary] = useState({ 
    cashIn: 0, cashOut: 0, cogs: 0, grossProfit: 0,
    expenses: 0, netProfit: 0, openingBalance: 0, closingBalance: 0,
    cashOutToOffice: 0, cash: 0, cityBank: 0, bkash1818: 0,
    currentDue: 0, dueCollection: 0
  });
  const dispatch = useDispatch();
  
  var formatter = new Intl.NumberFormat("en-IN");

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const fetchReport = () => {
    setLoading(true);
    const start = moment(selectedMonth).startOf('month').format("YYYY-MM-DD");
    const end = moment(selectedMonth).endOf('month').format("YYYY-MM-DD");
    
    console.log("Fetching report for:", start, "to", end);
    console.log("Fetching report for:", start, "to", end);
    dispatch(getInvoicePaymentsReport(
        start + "T00:00:00",
        end + "T23:59:59",
        "", "", "", "", "", "", "", 1, 1
    )).then((res) => {
        if (res) {
            setCashSummary(prev => ({
                ...prev,
                cashIn: res.total_receive || 0,
                cashOut: res.total_refund || 0,
            }));
        }
    }).catch(err => {
        console.error("Error fetching invoice payments report:", err);
    });

    getNewBusinessReportByMonth(start, end)
      .then((res) => {
        console.log("API Response:", res);
        if (res && res.data) {
          console.log("Data received:", res.data);
          console.log("Number of days:", res.data.length);
          
          // Transform the data for the table
          const tableData = res.data.map((day, index) => {
            const row = {
              key: index,
              date: day.date,
            };
            
            // Add all metrics dynamically
            day.metrics.forEach(metric => {
              row[metric.name] = metric.amount;
            });
            
            return row;
          });
          console.log("Transformed table data:", tableData);
          setDailyData(tableData);
          
          const totalRow = tableData.find(row => row.date === "Total");
          if (totalRow) {
            setCashSummary(prev => ({
              ...prev,
              cogs: totalRow["COG"] || 0,
              grossProfit: totalRow["Gross Profit"] || 0,
              expenses: totalRow["Expenses"] || 0,
              netProfit: totalRow["Net Profit"] || 0,
              openingBalance: totalRow["Opening balance"] || 0,
              closingBalance: totalRow["Closing balance"] || 0,
              cashOutToOffice: totalRow["Cash out to office"] || 0,
              cash: totalRow["Cash"] || 0,
              cityBank: totalRow["City Bank"] || 0,
              bkash1818: totalRow["Bkash-1818"] || 0,
              currentDue: totalRow["Current Due"] || 0,
              dueCollection: totalRow["Due collection"] || 0
            }));
          } else {
            let cogsTotal = 0, gpTotal = 0, expTotal = 0, npTotal = 0;
            let obTotal = 0, cbTotal = 0, cotoTotal = 0, cashTotal = 0;
            let cbkTotal = 0, bkashTotal = 0, cdTotal = 0, dcTotal = 0;
            tableData.forEach(row => {
              if (row.date !== "Total") {
                cogsTotal += row["COG"] || 0;
                gpTotal += row["Gross Profit"] || 0;
                expTotal += row["Expenses"] || 0;
                npTotal += row["Net Profit"] || 0;
                obTotal += row["Opening balance"] || 0;
                cbTotal += row["Closing balance"] || 0;
                cotoTotal += row["Cash out to office"] || 0;
                cashTotal += row["Cash"] || 0;
                cbkTotal += row["City Bank"] || 0;
                bkashTotal += row["Bkash-1818"] || 0;
                cdTotal += row["Current Due"] || 0;
                dcTotal += row["Due collection"] || 0;
              }
            });
            setCashSummary(prev => ({
              ...prev,
              cogs: cogsTotal,
              grossProfit: gpTotal,
              expenses: expTotal,
              netProfit: npTotal,
              openingBalance: obTotal,
              closingBalance: cbTotal,
              cashOutToOffice: cotoTotal,
              cash: cashTotal,
              cityBank: cbkTotal,
              bkash1818: bkashTotal,
              currentDue: cdTotal,
              dueCollection: dcTotal
            }));
          }

          const expenseNames = new Set();
          res.data.forEach(day => {
            day.metrics.forEach(metric => {
              if (metric.type === 'expense_breakdown') {
                expenseNames.add(metric.name);
              }
            });
          });

          const baseColumns1 = [
            {
              title: "Date",
              dataIndex: "date",
              key: "date",
              width: 120,
              fixed: 'left',
              render: (date) => date === "Total" ? <strong style={{color: '#1890ff'}}>Total</strong> : moment(date).format("DD MMM YYYY"),
            },
            
            {
              title: "Stock in",
              dataIndex: "Stock In",
              key: "Stock In",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Stock out",
              dataIndex: "Stock Out",
              key: "Stock Out",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Sell",
              dataIndex: "Sell",
              key: "Sell",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
             {
              title: "Refund",
              dataIndex: "Refund",
              key: "Refund",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "COGS",
              dataIndex: "COG",
              key: "COG",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Gross profit",
              dataIndex: "Gross Profit",
              key: "Gross Profit",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
          ];

          const expenseCols = Array.from(expenseNames).map(name => ({
            title: name,
            dataIndex: name,
            key: name,
            align: "right",
            render: (value) => formatter.format(value || 0),
          }));

          const baseColumns2 = [
            {
              title: "Total Expenses",
              dataIndex: "Expenses",
              key: "Expenses",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Net profit",
              dataIndex: "Net Profit",
              key: "Net Profit",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Current Due",
              dataIndex: "Current Due",
              key: "Current Due",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Due collection",
              dataIndex: "Due collection",
              key: "Due collection",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
           
            {
              title: "Opening balance",
              dataIndex: "Opening balance",
              key: "Opening balance",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Cash",
              dataIndex: "Cash",
              key: "Cash",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "City Bank",
              dataIndex: "City Bank",
              key: "City Bank",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Bkash-1818",
              dataIndex: "Bkash-1818",
              key: "Bkash-1818",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Cash out to office",
              dataIndex: "Cash out to office",
              key: "Cash out to office",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
            {
              title: "Closing balance",
              dataIndex: "Closing balance",
              key: "Closing balance",
              align: "right",
              render: (value) => formatter.format(value || 0),
            },
          ];

          setTableColumns([...baseColumns1, ...expenseCols, ...baseColumns2]);
        } else {
          console.log("No data in response");
          setDailyData([]);
          setTableColumns([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching report:", error);
        setDailyData([]);
      })
      .finally(() => setLoading(false));
  };

  const handleMonthChange = (date) => {
    if (date) {
      setSelectedMonth(moment(date));
    }
  };

  const handleDownloadCSV = () => {
    const start = moment(selectedMonth).startOf('month').format("YYYY-MM-DD");
    const end = moment(selectedMonth).endOf('month').format("YYYY-MM-DD");
    downloadNewBusinessReportByMonthCSV(start, end);
  };


  return (
    <>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item>New Business report by month</Breadcrumb.Item>
        </Breadcrumb>
        <div className="site-layout-background main-frame">
          {/* <div style={{ color: '#666', fontSize: '14px', padding: "10px" }}>
            Showing report for: {selectedMonth.format("MMMM YYYY")}
          </div> */}
          <Card style={{ marginBottom: 16 }}>
            <Space size="large">
              <div>
                <label style={{ marginRight: 8, fontWeight: 500 }}>Select Month:</label>
                <DatePicker
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  picker="month"
                  format="YYYY-MM"
                />
              </div>
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
                Download CSV
              </Button>
              <ExcelFile element={<Button type="primary" style={{backgroundColor:"green", color:"white"}}>Excel Download</Button>}>
                <ExcelSheet data={dailyData} name="Report">
                  {tableColumns.map(col => (
                    <ExcelColumn label={col.title} value={col.dataIndex} key={col.key} />
                  ))}
                </ExcelSheet>
              </ExcelFile>
            </Space>
          </Card>

          {loading ? (
            <Card>
              <Skeleton active />
            </Card>
          ) : (
            <Card>
            <h3 style={{textAlign: "center"}}>Station - {selectedMonth.format("MMMM YYYY")}</h3>
            <Divider />
              <Table
                columns={tableColumns}
                dataSource={dailyData}
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                size="small"
                // summary={(pageData) => {
                //   let totalPreviousStock = 0;
                //   let totalNewPurchase = 0;
                //   let totalPurchaseReturn = 0;
                //   let totalSales = 0;
                //   let totalCurrentStock = 0;

                //   pageData.forEach(({ previousStock, newPurchase, purchaseReturn, sales, currentStock }) => {
                //     totalPreviousStock += previousStock;
                //     totalNewPurchase += newPurchase;
                //     totalPurchaseReturn += purchaseReturn;
                //     totalSales += sales;
                //     totalCurrentStock += currentStock;
                //   });

                //   return (
                //     <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                //       <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                //       <Table.Summary.Cell index={1} align="right">
                //         {formatter.format(totalPreviousStock)}
                //       </Table.Summary.Cell>
                //       <Table.Summary.Cell index={2} align="right">
                //         {formatter.format(totalNewPurchase)}
                //       </Table.Summary.Cell>
                //       <Table.Summary.Cell index={3} align="right">
                //         {formatter.format(totalPurchaseReturn)}
                //       </Table.Summary.Cell>
                //       <Table.Summary.Cell index={4} align="right">
                //         {formatter.format(totalSales)}
                //       </Table.Summary.Cell>
                //       <Table.Summary.Cell index={5} align="right">
                //         {formatter.format(totalCurrentStock)}
                //       </Table.Summary.Cell>
                //     </Table.Summary.Row>
                //   );
                // }}
              />
              <div style={{ marginTop: '20px', width: '300px' }}>
                <Table
                  columns={[
                    { title: 'Particular', dataIndex: 'particular', key: 'particular', render: text => <strong>{text}</strong> },
                    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (val) => <strong>{formatter.format(val || 0)}</strong> }
                  ]}
                  dataSource={[
                    { key: '1', particular: 'Sell (+)', amount: cashSummary.cashIn },
                    { key: '2', particular: 'Refund (-)', amount: cashSummary.cashOut },
                    { key: '3', particular: 'COGS (-)', amount: cashSummary.cogs },
                    { key: '4', particular: 'Gross Profit (===)', amount: cashSummary.grossProfit },
                    { key: '5', particular: 'Current Due', amount: cashSummary.currentDue },
                    { key: '6', particular: 'Due Collection', amount: cashSummary.dueCollection },
                    { key: '7', particular: 'Expenses (-)', amount: cashSummary.expenses },
                    { key: '8', particular: 'Net Profit (===)', amount: cashSummary.netProfit },
                    { key: '9', particular: 'Opening Balance', amount: cashSummary.openingBalance },
                    { key: '10', particular: 'Cash out to office (-)', amount: cashSummary.cashOutToOffice },
                    { key: '11', particular: 'Closing Balance', amount: cashSummary.closingBalance },
                    { key: '12', particular: 'Cash', amount: cashSummary.cash },
                    { key: '13', particular: 'Bank', amount: cashSummary.cityBank },
                    { key: '14', particular: 'Bkash', amount: cashSummary.bkash1818 }
                  ]}
                  pagination={false}
                  bordered
                  size="small"
                />
              </div>
            </Card>
          )}
        </div>
      

      
    </>
  );
};

export default NewBusinessReportByMonth;
